import React, { useState } from "react";
import { Box, FormControl, Select, MenuItem, Button, Typography, Chip } from "@mui/material";
import { useSystemTheme } from "context/SystemThemeContext";

const PriorityEditor = React.memo(({ member, saving, onUpdatePriority }) => {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(member.priority || 5);

  const handleSave = async () => {
    await onUpdatePriority(member.calendar_id, editValue);
    setIsEditing(false);
  };

  const getPriorityLabel = (value) => {
    const priorityOptions = [
      { label: "High", value: 1 },
      { label: "Medium", value: 5 },
      { label: "Low", value: 10 },
    ];
    const option = priorityOptions.find(opt => opt.value === value);
    return option ? option.label : "Medium";
  };

  const getPriorityColor = (value) => {
    switch(value) {
      case 1: return "#d32f2f";
      case 5: return "#ed6c02";
      case 10: return "#2e7d32";
      default: return "#666666";
    }
  };

  const priorityOptions = [
    { label: "High", value: 1 },
    { label: "Medium", value: 5 },
    { label: "Low", value: 10 },
  ];

  if (isEditing) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            displayEmpty
            sx={{
              backgroundColor: isDarkMode ? '#2d2d3f' : '#fff',
              borderRadius: '6px',
              color: isDarkMode ? '#e2e8f0' : undefined,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDarkMode ? '#4a5568' : undefined,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#219297',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#219297',
              },
            }}
          >
            {priorityOptions.map((option) => (
              <MenuItem 
                key={option.value} 
                value={option.value}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: isDarkMode ? '#e2e8f0' : undefined,
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#2d2d3f' : undefined,
                  },
                }}
              >
                <Typography variant="body2">
                  {option.label}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ 
            minWidth: 'auto', 
            padding: '4px 8px',
            backgroundColor: '#219297',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#197f85',
            },
            '&:disabled': {
              backgroundColor: isDarkMode ? '#2d2d3f' : '#cccccc',
              color: isDarkMode ? '#a0aec0' : '#666666',
            },
          }}
        >
          Save
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setIsEditing(false)}
          sx={{ 
            minWidth: 'auto', 
            padding: '4px 8px',
            color: isDarkMode ? '#a0aec0' : '#000000',
            borderColor: isDarkMode ? '#4a5568' : undefined,
            '&:hover': {
              borderColor: '#219297',
              color: '#219297',
              backgroundColor: isDarkMode ? '#2d2d3f' : undefined,
            },
          }}
        >
          Cancel
        </Button>
      </Box>
    );
  }
  
  return (
    <Chip 
      label={`${getPriorityLabel(member.priority)}`}
      size="small"
      sx={{ 
        backgroundColor: `${getPriorityColor(member.priority)}15`,
        color: getPriorityColor(member.priority),
        fontSize: '0.7rem',
        fontWeight: 500,
        border: `1px solid ${getPriorityColor(member.priority)}30`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
        },
      }}
      onClick={() => setIsEditing(true)}
    />
  );
});

export default PriorityEditor;